import * as React from 'react';
import {action} from 'mobx';
import {observer} from 'mobx-react';
import {TextField, DatePicker, Label} from 'office-ui-fabric-react';

import DateTimeService from '@app/Services/DateTimeService';
import {DateTimePickerService} from '@app/Services/DateTimePickerService';

import SelectFilter, {ISelectArguments} from '../../../Components/SelectFilter';
import {IDisabledProps} from '@app/Models/IDisabledProps';
import {CountryModel, PersonInfoModel} from '@app/Models';
import {ChangeFieldCallback} from '../PersonInfoStore';
import {securityService} from '@app/Services/SecurityService';
import {Permissions} from '@app/AppConstants';
import {AzureAuth} from '@app/Components/AzureAuth';

type IdentificationBlockProps = IDisabledProps & {
    personInfo: PersonInfoModel;
    nationalities: CountryModel[];
    onChangeFieldHandler: ChangeFieldCallback;
};

@observer
export class IdentificationBlock extends React.Component<IdentificationBlockProps, {}> {

    render() {
        const {personInfo, nationalities} = this.props;
        if (!personInfo) return;
        const {hasPermission, hasAnyPermission, permissionCss} = securityService;
        return (
            <fieldset className="person-info-identification">
                <legend>Identification</legend>
                <div className="row">
                    <div className="col-2"/>
                    {hasAnyPermission([Permissions.ViewPersonNationality, Permissions.ViewPersonSecondNationality]) &&
                    <div className="col-4">
                        <Label>Country</Label>
                    </div>
                    }
                    {hasAnyPermission([Permissions.ViewPersonPassportNumber, Permissions.ViewPersonSecondPassportNumber]) &&
                    <div className="col-3">
                        <Label>Number</Label>
                    </div>
                    }
                    {hasAnyPermission([Permissions.ViewPersonSecondPassportExpiration, Permissions.ViewPersonPassportExpiration, Permissions.ViewPersonVisaRussiaExpiration, Permissions.ViewPersonVisaUsaCExpiration, Permissions.ViewPersonVisaUsaBExpiration]) &&
                    <div className="col-3">
                        <Label>Valid until</Label>
                    </div>
                    }
                </div>
                <div className="row">
                    {hasPermission(Permissions.ViewPersonNationality) &&
                    <>
                        <div className={'col-2' + permissionCss(Permissions.ViewPersonNationality)}>
                            <Label>Passport 1</Label><Label/>
                        </div>
                        <div className={'col-4' + permissionCss(Permissions.ViewPersonNationality, Permissions.EditPersonNationality)}>
                            <SelectFilter
                                title="Country"
                                placeholder="Select country"
                                value={personInfo.nationality || ''}
                                options={nationalities}
                                onSelect={this._onChangeNationality}
                                disabled={!hasPermission(Permissions.EditPersonNationality, !AzureAuth.isReadOnly)}
                            />
                        </div>
                    </>
                    }
                    {hasPermission(Permissions.ViewPersonPassportNumber) &&
                    <div className={'col-3' + permissionCss(Permissions.ViewPersonPassportNumber, Permissions.EditPersonPassportNumber)}>
                        <TextField
                            maxLength={11}
                            disabled={!hasPermission(Permissions.EditPersonPassportNumber, !AzureAuth.isReadOnly)}
                            onChange={this._onChangePassportNumber}
                            value={personInfo.passportNumber || ''}/>
                    </div>
                    }
                    {hasPermission(Permissions.ViewPersonPassportExpiration) &&
                    <div className={'col-3' + permissionCss(Permissions.ViewPersonPassportExpiration, Permissions.EditPersonPassportExpiration)}>
                        <DatePicker
                            className="date-field"
                            allowTextInput={hasPermission(Permissions.EditPersonPassportExpiration, !AzureAuth.isReadOnly)}
                            calloutProps={{hidden: !hasPermission(Permissions.EditPersonPassportExpiration, !AzureAuth.isReadOnly)}}
                            formatDate={DateTimePickerService.onFormatDate}
                            parseDateFromString={DateTimePickerService.onParseDateFromString}
                            onSelectDate={this._onPassportExpirationDate}
                            value={personInfo.passportExpirationDate || undefined}
                            disabled={!hasPermission(Permissions.EditPersonPassportExpiration, !AzureAuth.isReadOnly)}
                        />
                    </div>
                    }

                </div>
                <div className="row">
                    {hasPermission(Permissions.ViewPersonSecondNationality) &&
                    <>
                        <div className={'col-2' + permissionCss(Permissions.ViewPersonSecondNationality)}>
                            <Label>Passport 2</Label><Label/>
                        </div>
                        <div className={'col-4' + permissionCss(Permissions.ViewPersonSecondNationality, Permissions.EditPersonSecondNationality)}>
                            <SelectFilter
                                title="Country"
                                placeholder="Select country"
                                value={personInfo.secondNationality || ''}
                                options={nationalities}
                                onSelect={this._onChangeSecondNationality}
                                disabled={!hasPermission(Permissions.EditPersonSecondNationality, !AzureAuth.isReadOnly)}
                            />
                        </div>
                    </>
                    }
                    {hasPermission(Permissions.ViewPersonSecondPassportNumber) &&
                    <div className={'col-3' + permissionCss(Permissions.ViewPersonSecondPassportNumber, Permissions.EditPersonSecondPassportNumber)}>
                        <TextField
                            maxLength={11}
                            disabled={!hasPermission(Permissions.EditPersonSecondPassportNumber, !AzureAuth.isReadOnly)}
                            onChange={this._onChangeSecondPassportNumber}
                            value={personInfo.secondPassportNumber || ''}/>
                    </div>
                    }
                    {hasPermission(Permissions.ViewPersonSecondPassportExpiration) &&
                    <div className={'col-3' + permissionCss(Permissions.ViewPersonSecondPassportExpiration, Permissions.EditPersonSecondPassportExpiration)}>
                        <DatePicker
                            className="date-field"
                            allowTextInput={hasPermission(Permissions.EditPersonSecondPassportExpiration, !AzureAuth.isReadOnly)}
                            calloutProps={{hidden: !hasPermission(Permissions.EditPersonSecondPassportExpiration, !AzureAuth.isReadOnly)}}
                            formatDate={DateTimePickerService.onFormatDate}
                            parseDateFromString={DateTimePickerService.onParseDateFromString}
                            onSelectDate={this._onSecondPassportExpirationDate}
                            value={personInfo.secondPassportExpirationDate || undefined}
                            disabled={!hasPermission(Permissions.EditPersonSecondPassportExpiration, !AzureAuth.isReadOnly)}
                        />
                    </div>
                    }
                </div>
                <div className="row">
                    {hasPermission(Permissions.ViewPersonVisaUsaBExpiration) &&
                    <>
                        <div className={'col-2' + permissionCss(Permissions.ViewPersonVisaUsaBExpiration)}>
                            <Label>USA B1/B2</Label><Label/>
                        </div>
                        <div className={'col-7' + permissionCss(Permissions.ViewPersonVisaUsaBExpiration)}/>
                        <div className={'col-3' + permissionCss(Permissions.ViewPersonVisaUsaBExpiration, Permissions.EditPersonVisaUsaBExpiration)}>
                            <DatePicker
                                className="date-field"
                                allowTextInput={hasPermission(Permissions.EditPersonVisaUsaBExpiration, !AzureAuth.isReadOnly)}
                                calloutProps={{hidden: !hasPermission(Permissions.EditPersonVisaUsaBExpiration, !AzureAuth.isReadOnly)}}
                                formatDate={DateTimePickerService.onFormatDate}
                                parseDateFromString={DateTimePickerService.onParseDateFromString}
                                onSelectDate={this._onVisaUsaBExpirationDate}
                                value={personInfo.visaUsaBExpirationDate || undefined}
                                disabled={!hasPermission(Permissions.EditPersonVisaUsaBExpiration, !AzureAuth.isReadOnly)}
                            />
                        </div>
                    </>
                    }
                </div>
                <div className="row">
                    {hasPermission(Permissions.ViewPersonVisaUsaCExpiration) &&
                    <>
                        <div className={'col-2' + permissionCss(Permissions.ViewPersonVisaUsaCExpiration)}>
                            <Label>USA C1/0</Label><Label/>
                        </div>
                        <div className={'col-7' + permissionCss(Permissions.ViewPersonVisaUsaCExpiration)}/>
                        <div className={'col-3' + permissionCss(Permissions.ViewPersonVisaUsaCExpiration, Permissions.EditPersonVisaUsaCExpiration)}>
                            <DatePicker
                                className="date-field"
                                allowTextInput={hasPermission(Permissions.EditPersonVisaUsaCExpiration, !AzureAuth.isReadOnly)}
                                calloutProps={{hidden: !hasPermission(Permissions.EditPersonVisaUsaCExpiration, !AzureAuth.isReadOnly)}}
                                formatDate={DateTimePickerService.onFormatDate}
                                parseDateFromString={DateTimePickerService.onParseDateFromString}
                                onSelectDate={this._onVisaUsaCExpirationDate}
                                value={personInfo.visaUsaCExpirationDate || undefined}
                                disabled={!hasPermission(Permissions.EditPersonVisaUsaCExpiration, !AzureAuth.isReadOnly)}
                            />
                        </div>
                    </>
                    }
                </div>
                <div className="row">
                    {hasPermission(Permissions.ViewPersonVisaRussiaExpiration) &&
                    <>
                        <div className={'col-2' + permissionCss(Permissions.ViewPersonVisaRussiaExpiration)}>
                            <Label>Russia</Label><Label/>
                        </div>
                        <div className={'col-7' + permissionCss(Permissions.ViewPersonVisaRussiaExpiration)}/>
                        <div className={'col-3' + permissionCss(Permissions.ViewPersonVisaRussiaExpiration, Permissions.EditPersonVisaRussiaExpiration)}>
                            <DatePicker
                                className="date-field"
                                allowTextInput={hasPermission(Permissions.EditPersonVisaRussiaExpiration, !AzureAuth.isReadOnly)}
                                calloutProps={{hidden: !hasPermission(Permissions.EditPersonVisaRussiaExpiration, !AzureAuth.isReadOnly)}}
                                formatDate={DateTimePickerService.onFormatDate}
                                parseDateFromString={DateTimePickerService.onParseDateFromString}
                                onSelectDate={this._onVisaRussiaExpirationDate}
                                value={personInfo.visaRussiaExpirationDate || undefined}
                                disabled={!hasPermission(Permissions.EditPersonVisaRussiaExpiration, !AzureAuth.isReadOnly)}
                            />
                        </div>
                    </>
                    }
                </div>
                <div className="row">
                    {hasPermission(Permissions.ViewPersonCmcDate) &&
                        <>
                            <div className={'col-2' + permissionCss(Permissions.ViewPersonCmcDate)}>
                                <Label>CMC</Label><Label />
                            </div>
                            <div className={'col-7' + permissionCss(Permissions.ViewPersonCmcDate)} />
                            <div className={'col-3' + permissionCss(Permissions.ViewPersonCmcDate, Permissions.EditPersonCmcDate)}>
                                <DatePicker
                                    className="date-field"
                                    allowTextInput={false}
                                    calloutProps={{ hidden: true }}
                                    formatDate={DateTimePickerService.onFormatDate}
                                    parseDateFromString={DateTimePickerService.onParseDateFromString}
                                    value={personInfo.cmcDate || undefined}
                                disabled={true}
                                />
                            </div>
                        </>
                    }
                </div>
            </fieldset>
        );
    }

    @action.bound
    private _onPassportExpirationDate(date: Date | null | undefined): void {
        this.props.onChangeFieldHandler('passportExpirationDate', date ? DateTimeService.date(date.getFullYear(), date.getMonth(), date.getDate()) : undefined);
    }

    @action.bound
    private _onSecondPassportExpirationDate(date: Date | null | undefined): void {
        this.props.onChangeFieldHandler('secondPassportExpirationDate', date ? DateTimeService.date(date.getFullYear(), date.getMonth(), date.getDate()) : undefined);
    }

    @action.bound
    private _onVisaUsaBExpirationDate(date: Date | null | undefined): void {
        this.props.onChangeFieldHandler('visaUsaBExpirationDate', date ? DateTimeService.date(date.getFullYear(), date.getMonth(), date.getDate()) : undefined);
    }

    @action.bound
    private _onVisaUsaCExpirationDate(date: Date | null | undefined): void {
        this.props.onChangeFieldHandler('visaUsaCExpirationDate', date ? DateTimeService.date(date.getFullYear(), date.getMonth(), date.getDate()) : undefined);
    }

    @action.bound
    private _onVisaRussiaExpirationDate(date: Date | null | undefined): void {
        this.props.onChangeFieldHandler('visaRussiaExpirationDate', date ? DateTimeService.date(date.getFullYear(), date.getMonth(), date.getDate()) : undefined);
    }

    @action.bound
    private _onChangePassportNumber(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) {
        this.props.onChangeFieldHandler('passportNumber', newValue);
    }

    @action.bound
    private _onChangeSecondPassportNumber(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) {
        this.props.onChangeFieldHandler('secondPassportNumber', newValue);
    }

    @action.bound
    private _onChangeNationality(option: ISelectArguments) {
        if (option) {
            this.props.onChangeFieldHandler('nationality', option.value || '');
        }
    }

    @action.bound
    private _onChangeSecondNationality(option: ISelectArguments) {
        if (option) {
            this.props.onChangeFieldHandler('secondNationality', option.value || '');
        }
    }
}